import { Link } from 'react-router-dom';
import { t } from '../../i18n/i18n';

/**
 * A raised row inside an `InfoPanel`: name over a note, with an action at the
 * far end. An installer on Downloads, a support channel on Help Centre, a
 * platform on Account Setup — one object, three uses.
 *
 * Raised off the card rather than ruled off it: the Style Guide takes depth
 * from the surface step, and #111 is that step above `--bg-surface`.
 *
 * `logo` is a path under `public/images`, drawn as a real image rather than
 * through the app's `icon-mask` idiom. Masks exist so a glyph can take
 * `currentColor` for hover and active states; these are third-party brand
 * marks whose colour is the point, and a mask would flatten Windows' blue and
 * Android's four-colour mark to one grey.
 *
 * It is optional because the support channels have none — the row closes up
 * rather than leaving a hole where a logo would be.
 *
 * `href` makes it an outbound anchor, `to` an in-app link, neither a plain
 * div — a row that goes nowhere should not claim to.
 */
export default function InfoRow({ logo, label, note, action = 'Get', href, to }) {
  const body = (
    <>
      {logo && (
        <span className="ip-row__logo">
          {/* Decorative: the row's own name says which platform this is. */}
          <img src={logo} alt="" />
        </span>
      )}
      <span className="ip-row__copy">
        <span className="ip-row__name">{t(label)}</span>
        {note && <span className="ip-row__note">{t(note)}</span>}
      </span>
      {action && <span className="ip-row__go">{t(action)}</span>}
    </>
  );

  if (to) {
    return (
      <Link className="ip-row" to={to}>
        {body}
      </Link>
    );
  }

  if (href) {
    return (
      <a className="ip-row" href={href} target="_blank" rel="noreferrer noopener">
        {body}
      </a>
    );
  }

  return <div className="ip-row">{body}</div>;
}
